import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { LOGS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import { ScrollText, RefreshCw } from "lucide-react";

const levelDot = {
  info: "bg-slate-400",
  success: "bg-emerald-500",
  warn: "bg-amber-500",
  error: "bg-rose-500",
};

const levelBg = {
  info: "bg-slate-50",
  success: "bg-emerald-50",
  warn: "bg-amber-50",
  error: "bg-rose-50",
};

export default function Logs() {
  const [items, setItems] = useState([]);
  const [devices, setDevices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [l, d, a] = await Promise.all([api.logs({ limit: 300 }), api.devices(), api.accounts()]);
      setItems(l); setDevices(d); setAccounts(a);
    } catch { toast.error(t.common.failed); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deviceName = (id) => devices.find((d) => d.id === id)?.name;
  const accountUsername = (id) => accounts.find((a) => a.id === id)?.username;

  const filtered = filter === "all" ? items : items.filter((i) => i.level === filter);

  return (
    <div data-testid={LOGS.root}>
      <PageHeader
        title={t.logs.title}
        description={t.logs.description}
        actions={
          <button onClick={load} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 text-slate-700 text-sm font-medium transition-colors">
            <RefreshCw className="h-4 w-4" /> {t.common.refresh}
          </button>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        {Object.entries(t.logs.filter).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
              filter === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : filtered.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <ScrollText className="h-10 w-10 text-slate-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.logs.empty}</div>
        </div>
      ) : (
        <div className="card-soft divide-y divide-slate-100 overflow-hidden">
          {filtered.map((l) => (
            <div key={l.id} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50/60 transition-colors">
              <span className={`h-2 w-2 rounded-full shrink-0 ${levelDot[l.level] || levelDot.info}`} />
              <div className="text-[11px] font-mono text-slate-500 w-32 shrink-0">
                {new Date(l.ts).toLocaleString("ru-RU", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-900">{l.action}</span>
                  {l.device_id && <span className="text-[11px] text-slate-500">на {deviceName(l.device_id) || l.device_id.slice(0, 6)}</span>}
                  {l.account_id && <span className="text-[11px] text-slate-500">· {accountUsername(l.account_id) || l.account_id.slice(0, 6)}</span>}
                </div>
                <div className="text-xs text-slate-600 mt-0.5 truncate">{l.detail}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${levelBg[l.level] || levelBg.info}`}>
                {t.logs.filter[l.level] || l.level}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
